import { chakra, Flex, BoxProps, Icon, useToast, Button } from '@chakra-ui/react';
import { JSONValue } from '@twilio/conversations';
import { BaseEmoji } from 'emoji-mart';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';

import SendIcon from '@/assets/icons/ic-send-message.svg';

import { useChatDataContext } from './Chat.context';
import { ChatEmojiPicker } from './ChatMessageInput.elements';

const AutoResizeInput = chakra(TextareaAutosize);

interface IChatMessageInputProps extends BoxProps {
  messageAttributes: JSONValue;
}

export const ChatMessageInput: FC<IChatMessageInputProps> = ({ messageAttributes, ...rest }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef(null);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { conversation } = useChatDataContext();

  const onMessageSend = async () => {
    setIsLoading(true);
    const text = `${message}`.trim();

    // Text Message limit
    if (text.length >= 500) {
      toast({ title: t('conversation.errorSendingLongMessage'), status: 'error', isClosable: true });
      setIsLoading(false);
      return;
    }

    if (text !== '') {
      // eslint-disable-next-line prettier/prettier
      await conversation.sendMessage(text, messageAttributes);
      setMessage('');
    }
    setIsLoading(false);
    inputRef?.current.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onMessageSend();
    }
  };

  const onEmojiSelect = (emoji: BaseEmoji) => {
    setMessage(`${message}${emoji.native}`);
    const messageLength = message.length + emoji.native.length;

    inputRef.current.setSelectionRange(messageLength, messageLength);
    inputRef.current.focus();
  };

  return (
    <Flex
      alignItems="center"
      borderTop="1px"
      overflow="hidden"
      borderColor="gray.200"
      py={8}
      px={5}
      bgColor="white"
      {...rest}
    >
      <AutoResizeInput
        ref={inputRef}
        resize="none"
        w="full"
        outline="none"
        borderRadius="4px"
        px={2}
        mr={1}
        maxRows={5}
        disabled={isLoading}
        onKeyDown={onKeyDown}
        value={message}
        placeholder={t('conversation.sendMessagePlaceholder')}
        onChange={(event) => setMessage(event.target.value)}
      />

      <ChatEmojiPicker onEmojiSelect={onEmojiSelect} />
      <Button
        variant="link"
        colorScheme="violet"
        aria-label="Send message"
        alignSelf="center"
        maxW="min-content"
        minH={8}
        disabled={!message}
        onClick={onMessageSend}
        isLoading={isLoading}
      >
        <Icon w="24px" h="24px" ml={1} as={SendIcon} />
      </Button>
    </Flex>
  );
};
